import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';

import arrayMove from 'array-move';
import { convertDataFromServerToRedux } from 'src/pages/SalesChannelContentEditor/SalesChannelEditorProfilePage/tools';
import { msg } from 'src/tools/message';

import { ActionType } from 'src/types/common/common';

import { EnumCollapseId } from 'src/pages/SalesChannelContentEditor/SalesChannelEditorProfilePage/common';

import {
  getContentProfilePage,
  postContentProfilePage,
  postTranslateProfilePage,
} from '@/src/request/salesChannel/content';

import { getI18nFromGeneral } from '@/src/tools/tools';
import { isCurrentPrimary } from 'src/constrains/SalesChannelMultiLanguageSelect';
import {
  onCollapseOpen as _onCollapseOpen,
  hasError,
} from 'src/pages/SalesChannelContentEditor/slice/reduxCommonFunctions';
import {
  EnumReducers,
  getCollapseOpenStatus as _getCollapseOpenStatus,
  getCollapseValidationMsgDisplay as _getCollapseValidationMsgDisplay,
  getLoaded as _getLoaded,
  getSalesChannel as _getSalesChannel,
  getSalesChannelName as _getSalesChannelName,
  getState as _getState,
  getSubmitStamp as _getSubmitStamp,
  getTranslationState as _getTranslationState,
} from 'src/pages/SalesChannelContentEditor/slice/selectors';

export const fetchContentProfilePage = createAsyncThunk(
  'fetchSalesChannelContentProfilePage',
  async (data: any) => {
    const { uuid, params = {}, isClone } = data;
    const isPrimary = isCurrentPrimary();
    const response = await getContentProfilePage(uuid, params);
    return { response, isPrimary, isClone };
  },
);

export const submitContentProfilePage = createAsyncThunk(
  'submitSalesChannelContentProfilePage',
  async ({ body, params }: any) => {
    const isPrimary = isCurrentPrimary();
    const response = await postContentProfilePage(body, params);
    return { response, isPrimary };
  },
);

export const translateContentProfilePage = createAsyncThunk(
  'translateSalesChannelContentProfilePage',
  async ({ uuid, translationEnabled, multiLanguageParams }: any) => {
    const response = await postTranslateProfilePage(
      uuid,
      translationEnabled,
      multiLanguageParams,
    );
    return { response, translationEnabled };
  },
);

const defaultStatus = {
  isDataValid: false,
  hasModified: false,
};

const defaultState = {
  moduleContentOrder: [], //{两个不变参数moduleType,draggableId}
  collapseOpen: {
    [EnumCollapseId.backgroundStyle]: false,
    [EnumCollapseId.policy]: false,
    [EnumCollapseId.management]: false,
  },
  collapseErrorMsgDisplay: {
    [EnumCollapseId.backgroundStyle]: {
      hasError: false,
      display: false,
    },
    [EnumCollapseId.policy]: { hasError: false, display: false },
    [EnumCollapseId.management]: { hasError: false, display: false },
  },
  backgroundStyle: {
    image: '',
    backgroundColor: '#000000',
  },
  profileContentManagement: {},
  policy: [],
  contentUuid: null,
  salesChannelContent: {},
  salesChannelName: '',
  loaded: false,
  uuid: null,
  translationEnabled: false,
  translationLoading: false,
  ...defaultStatus,
};

function assData(state, data = {}) {
  Object.keys(data).forEach((i) => {
    state[i] = data[i];
  });
}

const salesChannelContentProfilePage = createSlice({
  name: 'salesChannelContentProfilePage',
  initialState: defaultState,
  reducers: {
    moduleContentOnChange(state, action) {
      state.hasModified = true;
      const { type, index, data } = action.payload;
      switch (type) {
        case ActionType.modify: {
          state.moduleContentOrder[index].titleTextColor = data;
          return;
        }
        case ActionType.reorder: {
          const { from, to } = data;
          console.log(from, to);
          state.moduleContentOrder = arrayMove(
            state.moduleContentOrder,
            from,
            to,
          );
          return;
        }

        default:
          return;
      }
    },

    onCollapseOpen(state, action) {
      const { idArr = [] } = action.payload; //=>[id:string]
      _onCollapseOpen(state, idArr);
    },

    onErrorChange(state, action) {
      const { id, status } = action.payload;
      state.collapseErrorMsgDisplay[id].hasError = status;
    },

    onBackgroundStyleChange(state, action) {
      state.hasModified = true;
      state.backgroundStyle = { ...state.backgroundStyle, ...action.payload };
    },

    onManagementChange(state, action) {
      state.hasModified = true;
      state.profileContentManagement = {
        ...state.profileContentManagement,
        ...action.payload,
      };
    },

    onPolicyChange(state, action) {
      const { type, data } = action.payload;
      state.hasModified = true;
      switch (type) {
        case ActionType.reselect: {
          const { newIndex, oldIndex } = data || {};
          const item = state.policy.splice(oldIndex, 1);
          state.policy.splice(newIndex, 0, item[0]);
          return;
        }
        case ActionType.modify: {
          state.policy = data;
          return;
        }
        case ActionType.delete: {
          state.policy.splice(data, 1);
          return;
        }
        case ActionType.add: {
          state.policy.push(data);
          return;
        }
        default:
          return;
      }
    },

    validation(state) {
      hasError(state, state.collapseErrorMsgDisplay || {});
    },
    resetData(state) {
      assData(state, defaultState);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchContentProfilePage.pending, (state) => {
      state.loaded = false;
    });
    builder.addCase(fetchContentProfilePage.rejected, (state) => {
      state.loaded = true;
    });
    builder.addCase(fetchContentProfilePage.fulfilled, (state, action) => {
      const { response, isPrimary, isClone } = action.payload;
      const _data = convertDataFromServerToRedux(response, isPrimary) || {};
      assData(state, _data);
      if (isClone) state.hasModified = true;
    });
    builder.addCase(submitContentProfilePage.pending, (state) => {
      state.loaded = false;
    });
    builder.addCase(submitContentProfilePage.rejected, (state) => {
      state.loaded = true;
    });
    builder.addCase(submitContentProfilePage.fulfilled, (state, action) => {
      const { response, isPrimary } = action.payload;
      const _data = convertDataFromServerToRedux(response, isPrimary) || {};
      msg.success(getI18nFromGeneral('general.status.changessavesuccessfully'));
      assData(state, { ..._data });
    });
    builder
      .addCase(translateContentProfilePage.pending, (state) => {
        state.translationLoading = true;
      })
      .addCase(translateContentProfilePage.fulfilled, (state, action) => {
        state.translationLoading = false;
        const { response, translationEnabled } = action.payload;
        if (response) {
          state.translationEnabled = translationEnabled;
        }
      })
      .addCase(translateContentProfilePage.rejected, (state) => {
        state.translationLoading = false;
      });
  },
});

export const getState = () => {
  return _getState(EnumReducers.profilePage);
};

export const getPagePolicy = () => {
  return createSelector(
    (state: any) => state.salesChannelContent.profilePage.policy,
    (policy) => {
      return policy;
    },
  );
};
export const getBackgroundStyle = () => {
  return createSelector(
    (state: any) => state.salesChannelContent.profilePage.backgroundStyle,
    (backgroundStyle) => {
      return backgroundStyle;
    },
  );
};

export const getManagement = () => {
  return createSelector(
    (state: any) =>
      state.salesChannelContent.profilePage.profileContentManagement,
    (profileContentManagement) => {
      return profileContentManagement;
    },
  );
};

export const getSubmitStamp = () => {
  return _getSubmitStamp(EnumReducers.profilePage);
};

export const getOrder = () => {
  return createSelector(
    (state: any) => state.salesChannelContent.profilePage.moduleContentOrder,
    (order) => {
      return order;
    },
  );
};

export const getHasModified = () => {
  return createSelector(
    (state: any) => state.salesChannelContent.profilePage.hasModified,
    (hasModified) => {
      return hasModified;
    },
  );
};

export const getOpeningCollapse = (order) => {
  return createSelector(
    (state: any) => {
      const _r = [];
      for (const item of order) {
        const { draggableId: id } = item;
        if (state.salesChannelContent.profilePage.collapseOpen[id]) {
          _r.push(id);
        }
      }
      return _r;
    },
    (status) => {
      return status;
    },
  );
};
export const getCollapseOpenStatus = (id) => {
  return _getCollapseOpenStatus(EnumReducers.profilePage, id);
};

export const getCollapseValidationMsgDisplay = (id) => {
  return _getCollapseValidationMsgDisplay(EnumReducers.profilePage, id);
};

export const getSalesChannel = () => {
  return _getSalesChannel(EnumReducers.profilePage);
};

export const getSalesChannelName = () => {
  return _getSalesChannelName(EnumReducers.profilePage);
};

export const getLoaded = () => {
  return _getLoaded(EnumReducers.profilePage);
};
export const getTranslationState = () => {
  return _getTranslationState(EnumReducers.profilePage);
};
export const {
  moduleContentOnChange,
  onBackgroundStyleChange,
  onCollapseOpen,
  onPolicyChange,
  validation,
  onErrorChange,
  resetData,
  onManagementChange,
} = salesChannelContentProfilePage.actions;

export default salesChannelContentProfilePage.reducer;
